import React, {useEffect, useMemo, useState} from 'react';
import type {UIMatch} from 'react-router-dom';
import {Outlet, useMatches} from 'react-router-dom';
import {useMediaQuery} from '@mui/material';
import {PageTitleKeys} from '../../../config/routeConstants';
import {usePageTitle} from '../../../hooks/usePageTitle';
import NavBar from '../NavBar';
import {usePageDescription} from '../../../hooks/usePageDescription';
import ResizeObserverErrorBoundary from '../../chat/ResizeObserverErrorBoundary';
import {OcrBatchProvider} from "../../../contexts/OcrBatchContext";
import {ConversationProvider} from "../../../contexts/ConversationContext";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import pl from "date-fns/locale/pl";
import enUS from "date-fns/locale/en-US";
import {useTranslation} from "react-i18next";
import Sidebar from "../Sidebar";
import {ContentWrapper, LayoutWrapper, MainContent} from "./Layout.styled";
import {breakpoints} from "../../../theme";

interface RouteHandle {
    titleKey?: PageTitleKeys;
}

const Layout = () => {
    const {i18n} = useTranslation();

    const isMobile = useMediaQuery(`(max-width: ${breakpoints.md})`);
    const [isSidebarOpen, setSidebarOpen] = useState(!isMobile);
    const matches = useMatches();

    const lastMatch = matches[matches.length - 1] as UIMatch<unknown, RouteHandle> | undefined;
    const titleKey = lastMatch?.handle?.titleKey || PageTitleKeys.Default;

    usePageTitle(titleKey);
    usePageDescription(titleKey);

    const lang = useMemo(
        () => (i18n.language || 'en').split('-')[0].toLowerCase(),
        [i18n.language],
    );
    const dfnsLocale = useMemo(() => (lang === 'pl' ? pl : enUS), [lang]);

    useEffect(() => {
        if (isMobile) {
            setSidebarOpen(false);
        } else {
            setSidebarOpen(true);
        }
    }, [isMobile]);

    const handleSidebarToggle = () => {
        setSidebarOpen(!isSidebarOpen);
    };

    return (
        <ResizeObserverErrorBoundary>
            <LayoutWrapper>
                <NavBar isSidebarOpen={isSidebarOpen}/>
                <ContentWrapper>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dfnsLocale}>
                    <ConversationProvider>
                    <OcrBatchProvider>
                        <Sidebar isOpen={isSidebarOpen} onToggle={handleSidebarToggle}/>
                        <MainContent $sidebarOpen={isSidebarOpen} $isMobile={isMobile}>
                            <Outlet/>
                        </MainContent>
                    </OcrBatchProvider>
                    </ConversationProvider>
                    </LocalizationProvider>
                </ContentWrapper>
            </LayoutWrapper>
        </ResizeObserverErrorBoundary>
    );
};

export default Layout;
